// Quick guide — Advanced Word Order (A2). The basic guide covers Subject-Verb-
// Object; this one adds the pieces that trip learners up once sentences grow:
// where adjectives sit, how clitic pronouns attach to the verb (enclisis) and
// when they move in front (proclisis), how two pronouns fuse, and where não and
// adverbs sit. Hand-drawn paper style with audio on every example.
import { speakButtonHTML } from "../lib/audio.js";

// One worked example: the Portuguese (with the moving part in <b>), an English
// gloss, and an optional note. Audio reads the plain Portuguese.
function ex(pt, en, note) {
  const plain = pt.replace(/<[^>]+>/g, "");
  return `
    <div class="mt-2">
      <p class="text-sage-800">${pt}${speakButtonHTML(plain)} <span class="text-base text-sage-500" style="font-family:Inter,sans-serif">— ${en}</span></p>
      ${note ? `<p class="text-sm text-sage-500" style="font-family:Inter,sans-serif">${note}</p>` : ""}
    </div>`;
}

export function advancedWordOrderView(app) {
  app.innerHTML = `
    <section class="max-w-3xl mx-auto fade-in">
      <h2 class="text-4xl font-script text-sage-800 mb-1">Advanced Word Order 🪡</h2>
      <p class="text-sage-600 mb-4" style="font-family:Inter,sans-serif">Once your sentences carry an object, a pronoun, or a description, Portuguese puts the pieces in places English doesn't. These are the key patterns that matter most at A2.</p>

      <div class="paper sketch-box p-5 sm:p-7 font-hand text-lg space-y-5">

        <div>
          <h3 class="text-2xl text-emerald-700 sketch-underline inline-block">1 · Adjectives go after the noun</h3>
          <p class="text-sage-700 mt-1">The default is <b>noun + adjective</b> — the opposite of English — and the adjective agrees in gender and number.</p>
          ${ex("um carro <b>vermelho</b>", "a red car")}
          ${ex("as casas <b>brancas</b>", "the white houses", "Plural noun → plural adjective.")}
          <p class="text-sage-700 mt-3">A few short, common ones go <i>before</i>: <b>bom, mau, grande, pequeno</b>, and numbers / quantities.</p>
          ${ex("um <b>bom</b> amigo · <b>muitos</b> livros", "a good friend · many books")}
          <p class="text-sage-700 mt-3">And a handful change meaning by position:</p>
          <div class="flex flex-wrap gap-x-6 gap-y-1 mt-1 text-base">
            <span><b>um grande homem</b> a great man · <b>um homem grande</b> a big man</span>
            <span><b>um velho amigo</b> a longtime friend · <b>um amigo velho</b> an elderly friend</span>
          </div>
        </div>

        <div>
          <h3 class="text-2xl text-amber-700 sketch-underline inline-block">2 · Object pronouns attach to the verb</h3>
          <p class="text-sage-700 mt-1">Unlike English, the unstressed object pronoun (me, te, o, a, nos, vos, lhe, lhes) normally attaches <b>after</b> the verb with a hyphen (enclisis).</p>
          ${ex("<b>Vejo-o</b>.", "I see him / it.", "Not “o vejo” in a neutral statement.")}
          ${ex("A Maria <b>chama-me</b>.", "María calls me.")}
          <p class="text-sage-700 mt-3">But several triggers pull it <b>in front</b> of the verb (proclisis) — negation, <i>que</i>, and adverbs like <i>já, sempre, também</i>:</p>
          ${ex("<b>Não me</b> chama.", "She doesn't call me.", "“não” forces the pronoun forward.")}
          ${ex("Já <b>te</b> disse.", "I already told you.", "“já” triggers proclisis too.")}
        </div>

        <div>
          <h3 class="text-2xl text-sky-700 sketch-underline inline-block">3 · Two pronouns fuse into one</h3>
          <p class="text-sage-700 mt-1">When you have both — <i>to whom</i> and <i>what</i> — the indirect and direct pronouns merge into a single form.</p>
          ${ex("Ela <b>dá-mo</b>.", "She gives it to me.", "me + o → mo.")}
          ${ex("<b>Mando-tos</b>?", "Shall I send them to you?", "te + os → tos.")}
        </div>

        <div>
          <h3 class="text-2xl text-rose-600 sketch-underline inline-block">4 · lhe / lhes fuse with o/a/os/as → lho, lha…</h3>
          <p class="text-sage-700 mt-1">When the indirect <b>lhe</b> or <b>lhes</b> meets a direct <b>o/a/os/as</b>, the two merge into a single word.</p>
          ${ex("lhe + o → <b>lho</b> digo", "I tell it to him/her", "Not “lhe o digo”.")}
          ${ex("<b>Dei-lhas</b> a eles.", "I gave them to them.", "lhes + as → lhas; add “a eles” to be clear.")}
        </div>

        <div>
          <h3 class="text-2xl text-violet-700 sketch-underline inline-block">5 · Where “não” and adverbs sit</h3>
          <p class="text-sage-700 mt-1"><b>Não</b> comes right before the verb — and it also pulls any clitic pronoun in front of it.</p>
          ${ex("<b>Não</b> mo dá.", "He doesn't give it to me.", "não → pronoun → verb, all together.")}
          ${ex("<b>Chego</b> sempre tarde.", "I always arrive late.", "Frequency adverbs sit near the verb; tarde/cedo often at the end.")}
        </div>

      </div>
      <p class="text-xs text-sage-400 mt-4" style="font-family:Inter,sans-serif">Rule of thumb: clitic pronouns attach to the end of the verb by default, but jump in front after “não”, “que”, and many adverbs — and a combined pronoun (mo, to, lho) bundles “to-whom” and “what” into one word.</p>
    </section>`;
  window.scrollTo({ top: 0, behavior: "smooth" });
}
